import { AssetDownload } from "@/api/api";
import { toast } from "@/lib/instrumented-toast";
import { formatFileSize, formatRelativeTime } from "@/lib/utils";
import { ChevronDownIcon, ChevronRightIcon, CopyIcon } from "lucide-react";
import { useState } from "react";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Separator } from "./ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "./ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

interface DownloadMetadataDrawerProps {
  download: AssetDownload | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DownloadMetadataDrawer({ download, open, onOpenChange }: DownloadMetadataDrawerProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["asset", "download", "user"]), // Main sections expanded by default
  );

  if (!download) return null;

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const copyAsJson = () => {
    const jsonString = JSON.stringify(download, null, 2);
    navigator.clipboard.writeText(jsonString);
    toast.success("Download metadata copied as JSON");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      relative: formatRelativeTime(date),
      absolute: date.toLocaleString(navigator.language, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      }),
    };
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return "N/A";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge variant="default">Completed</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      case "initiated":
        return <Badge variant="secondary">Initiated</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatValue = (
    value: string | number | boolean | null | undefined | { relative: string; absolute: string },
  ) => {
    if (value === null || value === undefined || value === "") return "N/A";
    return String(value);
  };

  type MetadataField = {
    label: string;
    value: string | number | boolean | null | undefined | { relative: string; absolute: string };
    isBadge?: boolean;
    isError?: boolean;
    isDate?: boolean;
  };

  const metadataSections: { id: string; title: string; fields: MetadataField[] }[] = [
    {
      id: "asset",
      title: "Asset Information",
      fields: [
        { label: "Asset Name", value: download.asset_name },
        { label: "Version", value: download.asset_version },
        { label: "Asset Path", value: download.asset_path },
        { label: "Asset ID", value: download.asset_id },
        { label: "File Size", value: formatFileSize(download.file_size_bytes) },
      ],
    },
    {
      id: "user",
      title: "User Information",
      fields: [
        { label: "Email", value: download.user_email },
        { label: "User ID", value: download.user_id },
        { label: "Organization", value: download.organization_name },
        { label: "Organization ID", value: download.organization_id },
      ],
    },
    {
      id: "download",
      title: "Download Details",
      fields: [
        { label: "Download ID", value: download.id },
        { label: "Status", value: download.download_status, isBadge: true },
        { label: "Initiated At", value: formatDate(download.download_initiated_at), isDate: true },
        {
          label: "Completed At",
          value: download.download_completed_at ? formatDate(download.download_completed_at) : "N/A",
          isDate: !!download.download_completed_at,
        },
        { label: "Duration", value: formatDuration(download.download_duration_ms) },
        { label: "URL Generated At", value: formatDate(download.signed_url_generated_at), isDate: true },
      ],
    },
    {
      id: "technical",
      title: "Technical Details",
      fields: [
        { label: "IP Address", value: download.ip_address },
        { label: "User Agent", value: download.user_agent },
        { label: "Error Message", value: download.error_message, isError: true },
      ].filter((field) => field.value), // Only show fields that have values
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] overflow-y-auto sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>Download Details</SheetTitle>
          <SheetDescription>Complete metadata information for this download</SheetDescription>
        </SheetHeader>

        {/* Action Button */}
        <div className="mt-4">
          <Button variant="outline" className="w-full" onClick={copyAsJson}>
            <CopyIcon className="mr-2 h-4 w-4" />
            Copy as JSON
          </Button>
        </div>

        <Separator className="my-4" />

        <div className="space-y-2">
          {metadataSections.map((section) => (
            <Collapsible
              key={section.id}
              open={expandedSections.has(section.id)}
              onOpenChange={() => toggleSection(section.id)}
            >
              <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md bg-muted/50 px-3 py-2 transition-colors hover:bg-muted">
                <span className="text-sm font-semibold">{section.title}</span>
                {expandedSections.has(section.id) ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 px-3 py-2">
                {section.fields.map((field, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">{field.label}</span>
                      {field.value && field.value !== "" && field.value !== "N/A" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={() => copyToClipboard(String(field.value || ""), field.label)}
                        >
                          <CopyIcon className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="text-sm">
                      {field.isBadge ? (
                        getStatusBadge(String(field.value))
                      ) : field.isError ? (
                        <span className="break-all text-destructive">{formatValue(field.value)}</span>
                      ) : field.isDate && typeof field.value === "object" && field.value !== null ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help underline decoration-dotted">
                              {typeof field.value === "object" && field.value !== null && "relative" in field.value
                                ? field.value.relative
                                : ""}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {typeof field.value === "object" && field.value !== null && "absolute" in field.value
                                ? field.value.absolute
                                : ""}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="break-all">{formatValue(field.value)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>

        <Separator className="my-6" />

        <SheetFooter>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => copyToClipboard(download.asset_path, "Asset Path")}
          >
            <CopyIcon className="mr-2 h-4 w-4" />
            Copy Path
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
